import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";

import {
  AddCreatorPageWrapperStyles,
  TableWrapperStyles,
  PageScrollWrapperStyles
} from "pages/Creators/AddCreator/styled";
import Button from "components/Buttons/index";
import CommonTable from "components/Tables/CommonTable";
import {
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon
} from "@phosphor-icons/react";

import { useL1ImportFilesSlice } from "./slice/index"; // correct slice
import {
  l1InsertFileRecordUpload,
  selectDeleteL1File,
  selectL1ImportFiles,
  selectUploadedL1File
} from "./slice/selector";
import columns from "./column";
import FileUpload from "components/FileUpload/index";

const BulkImportCreators = () => {
  const dispatch = useDispatch();
  const { actions } = useL1ImportFilesSlice(); // correct actions
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { initialLoading, loading, files, total_files, pagination } =
    useSelector(selectL1ImportFiles);
  const { loading: insertFileLoading, success: insertSuccess } = useSelector(
    l1InsertFileRecordUpload
  );
  const { loading: deleteLoading, success: deleteSuccess } =
    useSelector(selectDeleteL1File);

  const {
    url: uploadFileURL,
    name,
    size,
    loading: uploadFileLoading
  } = useSelector(selectUploadedL1File);

  const urlState = useMemo(
    () => ({ page: Number(searchParams.get("page")) || 1 }),
    [searchParams]
  );

  const fetchData = (page = urlState.page) => {
    dispatch(
      actions.fetchL1ImportFiles({
        page,
        limit: 20
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, [searchParams, dispatch, actions]);

  const syncURL = (updates = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) return;
      if (value === "") params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => syncURL({ page: newPage });

  const tableColumns = useMemo(() => columns(), []);

  return (
    <PageScrollWrapperStyles>
      <AddCreatorPageWrapperStyles>
        <div className="header-wrapper">
          <div className="go-back-btn" onClick={() => navigate("/creators")}>
            <ArrowLeftIcon weight="bold" size={20} color="#1F1F1F" />
          </div>
          <h1 className="title">Bulk Import Creators</h1>
        </div>

        <div className="upload-wrapper">
          <h1 className="title">Upload Sheet</h1>

          <FileUpload
            label="Upload Asset"
            value={uploadFileURL ? { name, size } : null}
            isPreview={Boolean(uploadFileURL)}
            previewFileUrl={uploadFileURL}
            uploadLoading={uploadFileLoading}
            deleteLoading={deleteLoading}
            insertLoading={insertFileLoading}
            acceptedFormats={["text/csv"]}
            acceptedExtensions={[".csv"]}
            acceptedFormatsText="CSV"
            maxSize={10 * 1024 * 1024}
            maxSizeText="10 MB"
            onRemove={() => {
              dispatch(
                actions.deleteL1FileRequest({
                  url: uploadFileURL
                })
              );
            }}
            onChange={(file) => {
              dispatch(
                actions.uploadl1Files({
                  file
                })
              );
            }}
            onClick={() => {
              if (!uploadFileURL) return;
              dispatch(actions.insertL1FileRecord({ url: uploadFileURL }));
            }}
            disabledData={
              !uploadFileURL || insertFileLoading || uploadFileLoading
            }
          />
        </div>

        <TableWrapperStyles>
          <div className="table-header-wrapper">
            <h2 className="title">Upload History</h2>
            <Button
              size="M"
              variant="secondary"
              leftIcon={<ArrowCounterClockwiseIcon weight="regular" />}
              onClick={() => fetchData()} // correct action
            >
              Refresh
            </Button>
          </div>

          <CommonTable
            data={files}
            columns={tableColumns}
            loading={loading}
            pagination={pagination}
            total_count={total_files}
            initialLoading={initialLoading}
            page={urlState.page}
            limit={pagination?.limit}
            onPageChange={handlePageChange}
            tableClassName="bulk-import-history-table"
          />
        </TableWrapperStyles>
      </AddCreatorPageWrapperStyles>
    </PageScrollWrapperStyles>
  );
};

export default BulkImportCreators;
